
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AddAccountsModal from "@components/AddAccountsModal";
import { tasksApi } from "@api/tasks";
import axios from "axios";

function StatusPill({ type }) {
  const map = {
    active: "bg-[#EAF7EE] text-[#1F7A3A]",
    paused: "bg-[#FFF3D6] text-[#8A6A00]",
    banned: "bg-[#FFECEC] text-[#B91C1C]",
    inactive: "bg-[#FFECEC] text-[#B91C1C]",
  };
  const label = {
    active: "Активен",
    paused: "Приостановлен",
    banned: "Забанен",
    inactive: "Не работает",
  };
  return (
    <span className={["inline-flex items-center h-6 px-3 rounded-md text-[12px] font-medium", map[type] || map.active].join(" ")}>
      {label[type] || "Активен"}
    </span>
  );
}

export default function TaskAccountsPage() {
  const { uid: uidParam } = useParams();
  const uid = Number(uidParam);
  const navigate = useNavigate();

  const [task, setTask] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [openAdd, setOpenAdd] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const inTaskSet = useMemo(() => new Set((task?.taskAccountUids || []).map((x) => Number(x))), [task]);

  async function load() {
    setLoading(true);
    try {
      const [t, a] = await Promise.all([
        tasksApi.get(uid),
        axios.get("/api/accounts").then((r) => r.data),
      ]);
      setTask(t);
      setRows(Array.isArray(a) ? a : []);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!Number.isFinite(uid)) return;
    load().catch(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);

  async function updatePool(nextUids) {
    const unique = Array.from(new Set(nextUids.map((x) => Number(x)).filter((x) => Number.isFinite(x) && x > 0)));
    try {
      const res = await tasksApi.updatePool(uid, { taskAccountUids: unique });
      // API возвращает {success, task}
      setTask(res?.task || (await tasksApi.get(uid)));
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Ошибка обновления пула";
      alert(msg);
      // перезагрузим задачу чтобы UI синхронизировался
      setTask(await tasksApi.get(uid));
    }
  }

  async function toggleOne(accUid, checked) {
    const cur = Array.isArray(task?.taskAccountUids) ? task.taskAccountUids.map(Number) : [];
    const next = checked ? [...cur, Number(accUid)] : cur.filter((x) => Number(x) !== Number(accUid));
    await updatePool(next);
  }

  async function deleteAccount(accUid) {
    if (!window.confirm(`Удалить аккаунт uid=${accUid}?`)) return;
    await fetch(`/api/accounts/${accUid}`, { method: "DELETE" });
    await load();
  }

  const sorted = useMemo(() => {
    let arr = Array.isArray(rows) ? [...rows] : [];

    // ✅ По умолчанию показываем только аккаунты этой задачи
    if (!showAll) {
      arr = arr.filter((a) => inTaskSet.has(Number(a.uid)));
    }

    arr.sort((a, b) => {
      const ia = inTaskSet.has(Number(a.uid)) ? 0 : 1;
      const ib = inTaskSet.has(Number(b.uid)) ? 0 : 1;
      if (ia !== ib) return ia - ib;
      return Number(b.uid) - Number(a.uid);
    });
    return arr;
  }, [rows, inTaskSet, showAll]);

  return (
    <div className="min-h-screen bg-[#F3F4F6]">
      <main className="px-6 pb-14">
        <div className="mx-auto max-w-[1100px] pt-24">
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="h-10 px-4 rounded-[14px] bg-white border border-[#D6D9E0] text-[13px] font-semibold hover:bg-[#F9FAFB] transition"
            >
              ← Назад
            </button>

            <button
              type="button"
              onClick={() => setOpenAdd(true)}
              className="h-10 px-4 rounded-[14px] bg-[#2B7CFF] text-white text-[13px] font-semibold hover:opacity-90 transition"
            >
              Добавить аккаунты в эту задачу
            </button>
          </div>

          <div className="mt-5 bg-white rounded-[18px] border border-[#C8C8C8] shadow-[0_12px_28px_rgba(0,0,0,0.06)] p-5">
            <div className="flex items-center justify-between">
              <div className="text-[14px] font-semibold text-[#111827]">
                Аккаунты задачи #{uid} {task?.taskName ? `— ${task.taskName}` : ""}
              </div>
              <div className="text-[12px] text-[#6B7280]">
                В задаче: <span className="font-semibold text-[#111827]">{(task?.taskAccountUids || []).length}</span>
              </div>
            </div>

            <div className="mt-3 flex items-center justify-between gap-3">
              <div className="text-[12px] text-[#6B7280]">
                {showAll ? "Показываются: все аккаунты" : "Показываются: только аккаунты этой задачи"}
              </div>
              <button
                type="button"
                onClick={() => setShowAll((v) => !v)}
                className="h-9 px-4 rounded-[14px] border border-[#D6D9E0] bg-white text-[12px] font-semibold hover:bg-[#F9FAFB] transition"
              >
                {showAll ? "Показать только этой задачи" : "Показать все"}
              </button>
            </div>

            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="text-left text-[#6B7280]">
                    <th className="py-2 pr-3 w-[90px]">В задаче</th>
                    <th className="py-2 pr-3 w-[80px]">UID</th>
                    <th className="py-2 pr-3">Статус</th>
                    <th className="py-2 pr-3">Proxy UID</th>
                    <th className="py-2 pr-3">Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr>
                      <td className="py-4 text-[#6B7280]" colSpan={5}>
                        Загрузка...
                      </td>
                    </tr>
                  )}

                  {!loading && !sorted.length && (
                    <tr>
                      <td className="py-4 text-[#6B7280]" colSpan={5}>
                        Аккаунтов нет
                      </td>
                    </tr>
                  )}

                  {sorted.map((a) => {
                    const checked = inTaskSet.has(Number(a.uid));
                    const status = String(a.status || "active").toLowerCase();
                    const reservedOther = a.reservedTaskUid != null && Number(a.reservedTaskUid) !== uid;
                    return (
                      <tr key={a.uid} className="border-t border-[#EEF2F7]">
                        <td className="py-2 pr-3">
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={reservedOther}
                            onChange={(e) => toggleOne(a.uid, e.target.checked)}
                          />
                        </td>
                        <td className="py-2 pr-3 font-semibold text-[#111827]">{a.uid}</td>
                        <td className="py-2 pr-3">
                          <StatusPill type={status} />
                        </td>
                        <td className="py-2 pr-3 text-[#374151]">{a.proxyId}</td>
                        <td className="py-2 pr-3">
                          {reservedOther && (
                            <span className="mr-2 text-[12px] text-[#B91C1C] font-semibold">
                              Занят задачей #{a.reservedTaskUid}
                            </span>
                          )}
                          <button
                            type="button"
                            onClick={() => deleteAccount(a.uid)}
                            className="h-8 px-3 rounded-[12px] bg-[#FFECEC] text-[#B91C1C] text-[12px] font-semibold hover:opacity-90 transition"
                          >
                            Удалить
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <AddAccountsModal
            open={openAdd}
            onClose={() => setOpenAdd(false)}
            taskUid={uid}
            onAdded={async (data) => {
              const created = Array.isArray(data?.createdUids) ? data.createdUids : [];
              if (created.length) {
                const cur = Array.isArray(task?.taskAccountUids) ? task.taskAccountUids.map(Number) : [];
                await updatePool([...cur, ...created]);
              } else {
                await load();
              }
            }}
          />
        </div>
      </main>
    </div>
  );
}
